/* eslint-disable no-unused-expressions */
import { useEffect, useState } from 'react';
import AUX from '../../../hoc/Aux_';
import { Button } from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import {
  getMember,
  getProjects,
  getShareReport,
  getTrackedHourIdleMinuteWise,
  getTrackedHoursIdleMinuteWiseCSV,
} from '../../../redux/MainAction';
import { FaFileCsv } from 'react-icons/fa';
import { downloadExcel } from '../../../helpers/helpers';
import moment from 'moment';
import { toast } from 'react-toastify';
import { getDeployedUrl, getExeVersions } from '../../../Constant';
import Select from 'react-select';
import PageLoader from '../../../components/HOC/PageLoader';
import VersionSelector from '../../VersionManagement/version-selector';

const convertMinutesToHM = (minutes) => {
  if (!minutes || minutes === 0) return '0h : 0m';
  const hours = Math.floor(minutes / 60);
  const mins = Math.trunc(minutes % 60);
  return `${hours}h : ${mins}m`;
};

const generateDateRange = (startDate, endDate) => {
  const dates = [];
  const start = moment(startDate);
  const end = moment(endDate);

  while (start.isSameOrBefore(end)) {
    dates.push(start.format('YYYY-MM-DD'));
    start.add(1, 'day');
  }

  return dates;
};

const processTrackedHoursData = (trackedHours, dateRange) => {
  const employeeData = {};

  trackedHours?.forEach((record) => {
    const employeeId = record.employeeId;
    if (!employeeData[employeeId]) {
      employeeData[employeeId] = {
        employeeId: record.employeeId,
        employeeName: record.employeeName,
        method: record.method,
        dailyMinutes: {},
        totalMinutes: 0,
      };
    }

    employeeData[employeeId].dailyMinutes[record.activityDateLocal] =
      record.uniqueMinuteCount || 0;
    employeeData[employeeId].totalMinutes += record.uniqueMinuteCount || 0;
  });

  return Object.values(employeeData).map((emp) => ({
    ...emp,
    dates: dateRange.map((date) => emp.dailyMinutes[date] || 0),
    totalTime: convertMinutesToHM(emp.totalMinutes),
  }));
};

const TrackedHourIdleMinuteWise = () => {
  const dispatch = useDispatch();

  const [version, setVersion] = useState(getExeVersions()[0]?.value);
  const emp = useSelector((state) => state.Webtracker.member);
  const prj = useSelector((state) => state.Webtracker.projects);

  useEffect(() => {
    dispatch(getMember());
    dispatch(getProjects());
  }, []);

  return (
    <TrackedHourIdleMinuteWiseVersion
      key={version}
      version={version}
      emp={emp}
      prj={prj}
    >
      <VersionSelector version={version} setVersion={setVersion} />
    </TrackedHourIdleMinuteWiseVersion>
  );
};

const TrackedHourIdleMinuteWiseVersion = ({ version, children, emp, prj }) => {
  const [apiCalls, setApiCalls] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const dispatch = useDispatch();
  const trackedHour = useSelector(
    (state) => state?.Webtracker?.reportsTrackedHoursIdleMinuteWise
  );

  const [dateRange, setDateRange] = useState('today');
  const [showReset, setShowReset] = useState(false);

  const today = new Date();

  const [filters, setFilters] = useState({
    entityType: 'CONTRACT',
    durationType: 'DAY',
    data: 'HOURS',
    employeeIds: [],
    projectIds: '',
    startDate: moment(today).format('YYYY-MM-DD'),
    endDate: moment(today).format('YYYY-MM-DD'),
  });

  const [appliedFilters, setAppliedFilters] = useState({
    startDate: moment(today).format('YYYY-MM-DD'),
    endDate: moment(today).format('YYYY-MM-DD'),
  });

  const viewDates = generateDateRange(
    appliedFilters.startDate,
    appliedFilters.endDate
  );

  const processedData = processTrackedHoursData(trackedHour, viewDates);

  const onChangeFilter = (event) => {
    const { name, value } = event.target;
    setFilters((prevFilters) => ({
      ...prevFilters,
      [name]: value,
    }));
  };

  const handleDateRangeChange = (e) => {
    const value = e.target.value;
    setDateRange(value);
    let start, end;
    const today = new Date();

    const currentDayOfWeek = today.getDay();
    const monday = new Date(today);
    monday.setDate(
      today.getDate() - (currentDayOfWeek === 0 ? 6 : currentDayOfWeek - 1)
    );

    switch (value) {
      case 'today':
        start = end = today;
        break;
      case 'thisWeek':
        start = monday;
        end = today;
        break;
      case 'last7days':
        end = today;
        start = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case 'thisMonth':
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        end = today;
        break;
      case 'last30days':
        end = today;
        start = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
        break;
      case 'last2months':
        end = today;
        start = new Date(
          today.getFullYear(),
          today.getMonth() - 2,
          today.getDate()
        );
        break;
      case 'custom':
        return;
      default:
        return;
    }
    setFilters((prev) => ({
      ...prev,
      startDate: moment(start).format('YYYY-MM-DD'),
      endDate: moment(end).format('YYYY-MM-DD'),
    }));
  };

  const handleEmployeeChange = (selectedOptions) => {
    const selectedIds = selectedOptions
      ? selectedOptions.map((option) => option.value)
      : [];
    setFilters((prevFilters) => ({
      ...prevFilters,
      employeeIds: selectedIds,
    }));
  };

  const employeeOptions =
    emp?.rows?.map((member) => ({
      value: member.id,
      label: `${member.firstName} ${member.lastName}`,
    })) || [];

  const dispatchCalls = async (data) => {
    await dispatch(getTrackedHourIdleMinuteWise(data, version === '1.1.8'));
  };

  const handleApply = async (e) => {
    e.preventDefault();
    if (filters?.employeeIds?.length === 0 && version === '1.1.7') {
      toast.error('Please select at least one member to see the reports');
      return;
    }

    if (filters?.startDate && filters?.endDate) {
      const startDate = moment(filters.startDate);
      const endDate = moment(filters.endDate);

      if (startDate.isAfter(endDate)) {
        toast.error('Start date cannot be greater than End date', {
          position: 'top-right',
        });
        return;
      }
    }

    setApiCalls(true);
    setIsLoading(true);

    const payload = {
      entityType: 'CONTRACT',
      durationType: 'DAY',
      data: 'HOURS',
    };

    if (filters?.employeeIds?.length > 0) {
      payload.employeeIds = filters.employeeIds;
    }

    if (filters?.projectIds !== '') {
      payload.projectIds = [filters.projectIds];
    }
    if (filters?.startDate) {
      payload.startDate = filters.startDate;
    }

    if (filters?.endDate) {
      payload.endDate = filters.endDate;
    }

    setAppliedFilters({
      startDate: filters.startDate,
      endDate: filters.endDate,
    });

    await dispatchCalls(payload);
    setShowReset(true);
    setIsLoading(false);
  };

  const handleCopy = async () => {
    const payload = {
      entityType: 'CONTRACT',
      durationType: 'DAY',
      data: 'HOURS',
    };

    if (filters?.employeeIds?.length > 0) {
      payload.employeeIds = filters.employeeIds;
    }

    if (filters?.projectIds !== '') {
      payload.projectIds = [filters.projectIds];
    }
    if (filters?.startDate) {
      payload.startDate = filters.startDate;
    }

    if (filters?.endDate) {
      payload.endDate = filters.endDate;
    }

    const share = await dispatch(getShareReport(payload));
    const linkValue = `${getDeployedUrl()}/tracked-hours-reports/sharing/${share?.token}`;
    navigator.clipboard
      .writeText(linkValue)
      .then(() => {
        toast.success('Link copied to clipboard!', {
          position: 'top-right',
        });
      })
      .catch((err) => {
        console.error('Failed to copy the text: ', err);
      });
  };

  const handleReset = (e) => {
    e.preventDefault();
    const todayFormatted = moment(today).format('YYYY-MM-DD');

    const data = {
      startDate: todayFormatted,
      endDate: todayFormatted,
    };

    const payload = {
      entityType: 'CONTRACT',
      durationType: 'DAY',
      data: 'HOURS',
    };

    // dispatchCalls({ ...payload, ...data });

    setShowReset(false);
    setFilters({
      ...payload,
      employeeIds: [],
      projectIds: '',
      teamIds: '',
      startDate: moment(today).format('YYYY-MM-DD'),
      endDate: moment(today).format('YYYY-MM-DD'),
    });

    setAppliedFilters({
      startDate: todayFormatted,
      endDate: todayFormatted,
    });

    setDateRange('today');
  };

  async function downloadCSVHandler() {
    setIsLoading(true);
    const payload = {
      entityType: 'CONTRACT',
      durationType: 'DAY',
      data: 'HOURS',
    };

    if (filters?.employeeIds?.length > 0) {
      payload.employeeIds = filters.employeeIds;
    }

    if (filters?.projectIds) {
      payload.projectIds = [filters.projectIds];
    }
    if (filters?.startDate) {
      payload.startDate = filters.startDate;
    }

    if (filters?.endDate) {
      payload.endDate = filters.endDate;
    }

    const csv = await dispatch(getTrackedHoursIdleMinuteWiseCSV(payload, true));
    downloadExcel(csv, 'Tracked Hour Report');
    setIsLoading(false);
  }

  return (
    <PageLoader isLoading={isLoading}>
      <AUX>
        <div className="page-content-wrapper">
          <div className="container-fluid">
            <h5 className="text-muted"></h5>
            <div className="row">
              <div className="col-12">
                <div className="row">
                  <div className="col-12">
                    <div className="card m-b-20">
                      <div className="d-flex flex-column card-body">
                        <div
                          className="d-flex justify-content-start align-items-start"
                          style={{ gap: '10px' }}
                        >
                          <p className="text-muted mt-2 text-nowrap">
                            Filter by :
                          </p>

                          <div className="mb-3">
                            <div className="d-flex mb-2">
                              {children}
                              <div className="fillSelect">
                                <i className="mdi mdi-briefcase font-20 fillIcon"></i>
                                <select
                                  className="form-control font-14"
                                  name="projectIds"
                                  value={filters?.projectIds}
                                  onChange={onChangeFilter}
                                >
                                  <option value="">All Projects</option>
                                  {prj?.rows?.map((mems, idx) => (
                                    <option key={idx} value={mems?.id}>
                                      {mems?.name}
                                    </option>
                                  ))}
                                </select>
                              </div>

                              <div className="fillSelect">
                                <select
                                  className="form-control font-14"
                                  value={dateRange}
                                  onChange={handleDateRangeChange}
                                >
                                  <option value="today">Today</option>
                                  <option value="thisWeek">This Week</option>
                                  <option value="last7days">Last 7 Days</option>
                                  <option value="last30days">
                                    Last 30 Days
                                  </option>
                                  <option value="last2months">
                                    Last 2 Months
                                  </option>

                                  {version === '1.1.7' ? null : (
                                    <option value="thisMonth">
                                      This month
                                    </option>
                                  )}
                                  <option value="custom">Custom Date</option>
                                </select>
                              </div>

                              {dateRange === 'custom' && (
                                <div
                                  className="d-flex align-items-center font-14 ml-2"
                                  style={{ gap: '5px' }}
                                >
                                  <input
                                    type="date"
                                    className="form-control font-14"
                                    name="startDate"
                                    value={filters?.startDate}
                                    onChange={onChangeFilter}
                                    max={moment().format('YYYY-MM-DD')}
                                  />
                                  to
                                  <input
                                    type="date"
                                    className="form-control font-14"
                                    name="endDate"
                                    value={filters?.endDate}
                                    onChange={onChangeFilter}
                                    max={moment().format('YYYY-MM-DD')}
                                  />
                                </div>
                              )}
                            </div>

                            <div
                              className="d-flex align-items-start"
                              style={{ gap: '0.8rem' }}
                            >
                              <div className="fillSelect">
                                <i className="mdi mdi-account-multiple-outline font-20 fillIcon"></i>
                                <Select
                                  isMulti
                                  value={employeeOptions.filter((option) =>
                                    filters.employeeIds.includes(option.value)
                                  )}
                                  onChange={handleEmployeeChange}
                                  options={employeeOptions}
                                  placeholder="All Members"
                                  className="react-select-container"
                                  classNamePrefix="react-select"
                                  isClearable
                                  styles={{
                                    control: (provided) => ({
                                      ...provided,
                                      minHeight: '35px',
                                      height: '35px',
                                      fontSize: '14px',
                                      minWidth: '200px',
                                      minWidth: '260px',
                                      border: '1px solid #ced4da',
                                    }),
                                    valueContainer: (provided) => ({
                                      ...provided,
                                      height: '33px',
                                      padding: '0 4px',
                                      overflowY: 'auto',
                                    }),
                                    indicatorsContainer: (provided) => ({
                                      ...provided,
                                      height: '33px',
                                    }),
                                    multiValue: (provided) => ({
                                      ...provided,
                                      backgroundColor: '#e9ecef',
                                    }),
                                    multiValueLabel: (provided) => ({
                                      ...provided,
                                      fontSize: '12px',
                                    }),
                                    indicatorsContainer: (provided) => ({
                                      ...provided,
                                      height: '34px',
                                      padding: '0 4px',
                                    }),
                                  }}
                                />
                              </div>

                              <div className="d-flex" style={{ gap: '10px' }}>
                                <Button
                                  className="d-flex align-items-center justify-content-center"
                                  style={{ padding: '6px 14px' }}
                                  size="sm"
                                  color="primary"
                                  onClick={handleApply}
                                >
                                  <i className="ion-ios7-search font-20"></i>
                                </Button>

                                {showReset ? (
                                  <Button
                                    className="d-flex align-items-center justify-content-center"
                                    style={{ padding: '6px 13px' }}
                                    size="sm"
                                    color="danger"
                                    onClick={handleReset}
                                  >
                                    <i className="mdi mdi-close font-20"></i>
                                  </Button>
                                ) : (
                                  ''
                                )}
                              </div>
                            </div>
                          </div>
                        </div>

                        <span>
                          <b>Note :</b> All records are displayed based on the
                          timezone set by the admin.
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="card ">
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center m-b-20">
                      <div className=""></div>

                      <div className="d-flex">
                        <Button
                          outline
                          color="purple"
                          size="sm"
                          className="mr-2 d-flex align-items-center"
                          onClick={handleCopy}
                        >
                          <i className="mdi mdi-share-variant  mr-2"></i> Share
                          Report
                        </Button>

                        <Button
                          color="success"
                          size="sm"
                          className="mr-2 d-flex align-items-center"
                          onClick={() => downloadCSVHandler()}
                        >
                          <FaFileCsv className="mr-1" /> XLSX
                        </Button>
                      </div>
                    </div>
                    <div style={{ overflowX: 'auto' }}>
                      <table
                        id="datatable"
                        className="table dt-responsive nowrap table-vertical"
                        width="100%"
                        cellSpacing="0"
                      >
                        <thead>
                          <tr>
                            <th className="text-nowrap">Member</th>
                            <th className="text-nowrap">Method</th>
                            {viewDates?.map((date, idx) => (
                              <th key={idx} className="text-nowrap">
                                {moment(date).format('DD MMM YYYY')}
                              </th>
                            ))}
                            <th className="text-nowrap">Total Time</th>
                          </tr>
                        </thead>
                        <tbody>
                          {apiCalls ? (
                            processedData?.length > 0 ? (
                              processedData?.map((item, idx) => {
                                return (
                                  <tr key={idx}>
                                    <td>
                                      <div className="text-nowrap">
                                        <span className="border rounded p-1 text-white bg-info text-uppercase">
                                          {item?.employeeName?.charAt(0)}
                                        </span>
                                        <span className="ml-1">
                                          {item?.employeeName}
                                        </span>
                                      </div>
                                    </td>
                                    <td className="text-nowrap">
                                      {item?.method}
                                    </td>
                                    {item?.dates?.map((minutes, dateIdx) => (
                                      <td key={dateIdx} className="text-nowrap">
                                        {convertMinutesToHM(minutes)}
                                      </td>
                                    ))}
                                    <td className="text-nowrap">
                                      {item?.totalTime}
                                    </td>
                                  </tr>
                                );
                              })
                            ) : (
                              <tr>
                                <td
                                  colSpan={viewDates?.length + 3}
                                  className="text-center"
                                >
                                  No Records Found
                                </td>
                              </tr>
                            )
                          ) : (
                            <tr>
                              <td colSpan="10" className="text-center">
                                Please click on the search button.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </AUX>
    </PageLoader>
  );
};

export default TrackedHourIdleMinuteWise;
